import { useState, type ReactNode } from 'react';
import {
  ENVIRONMENT_LABELS,
  ENVIRONMENT_TOKENS,
  OPERATIONS,
  TYPED_TOKEN_HINTS,
  type BackupDto,
  type ConnectionDto,
} from '@visualitics-toolkit/shared';

import { Badge, Button, Checkbox, Field, Input } from '../atoms';
import { ErrorBlock, formatBytes, formatDateTime } from '../molecules';
import { useRestoreBackup } from '../../lib/queries';
import { Dialog } from './Dialog';

/**
 * Restore allowlist-restrito a local/staging (spec/11-BACKUP-RESTORE.md §8).
 * Exige digitar o nome do ambiente — mesma regra do executor para `db.restore`.
 */
export function RestoreDialog({
  backup,
  connection,
  onClose,
}: {
  backup: BackupDto;
  connection: ConnectionDto;
  onClose: () => void;
}): ReactNode {
  const restoreBlocked = connection.environment === 'production';
  const expectedToken = ENVIRONMENT_TOKENS[connection.environment];
  const [ackImpact, setAckImpact] = useState(false);
  const [typedToken, setTypedToken] = useState('');
  const [dropExisting, setDropExisting] = useState(true);
  const [doneMessages, setDoneMessages] = useState<string[] | null>(null);
  const restore = useRestoreBackup();

  const canSubmit =
    !restoreBlocked &&
    ackImpact &&
    typedToken.trim() === expectedToken &&
    !restore.isPending;

  return (
    <Dialog title="Restaurar backup" onClose={onClose}>
      {doneMessages ? (
        <div className="space-y-3">
          <p className="text-sm text-content">Restore concluído.</p>
          <ul className="list-inside list-disc text-sm text-muted">
            {doneMessages.map((message) => (
              <li key={message}>{message}</li>
            ))}
          </ul>
          <div className="flex justify-end">
            <Button variant="primary" onClick={onClose}>
              Fechar
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <dl className="grid gap-2 text-sm sm:grid-cols-2">
            <Info label="Arquivo" value={backup.filename ?? backup.id} />
            <Info label="Modo" value={backup.mode} />
            <Info label="Tamanho" value={formatBytes(backup.bytes)} />
            <Info label="Concluído em" value={formatDateTime(backup.finishedAt)} />
          </dl>

          <div className="rounded-md border border-severity-destructive/40 bg-severity-destructive/10 px-3 py-2 text-sm text-content">
            <p className="mb-1 font-medium">
              Severidade: {OPERATIONS['db.restore'].impact.severity}{' '}
              <Badge tone="danger">{ENVIRONMENT_LABELS[connection.environment]}</Badge>
            </p>
            <ul className="list-inside list-disc text-muted">
              {OPERATIONS['db.restore'].impact.consequences.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>

          {restoreBlocked ? (
            <ErrorBlock message="Restore em produção é bloqueado pela allowlist. Use o runbook manual (SSH + pg_restore/psql)." />
          ) : (
            <>
              <Checkbox
                label="Li o aviso e entendo que o conteúdo atual do banco alvo será sobrescrito."
                checked={ackImpact}
                onChange={(event) => setAckImpact(event.target.checked)}
              />

              <Field
                label={`Confirmação — ${TYPED_TOKEN_HINTS.environment}`}
                help={`Digite exatamente: ${expectedToken}`}
              >
                <Input
                  value={typedToken}
                  onChange={(event) => setTypedToken(event.target.value)}
                  autoComplete="off"
                  data-testid="restore-token"
                />
              </Field>

              <Checkbox
                label="Limpar objetos existentes antes (pg_restore --clean)"
                checked={dropExisting}
                onChange={(event) => setDropExisting(event.target.checked)}
              />
            </>
          )}

          {restore.error instanceof Error && <ErrorBlock message={restore.error.message} />}

          <div className="flex justify-end gap-2">
            <Button onClick={onClose}>Cancelar</Button>
            <Button
              variant="danger"
              data-testid="btn-restore-confirm"
              loading={restore.isPending}
              disabled={!canSubmit}
              onClick={() =>
                restore.mutate(
                  {
                    backupId: backup.id,
                    connectionId: connection.id,
                    confirmation: { ackImpact, typedToken },
                    dropExisting,
                  },
                  {
                    onSuccess: (result) => setDoneMessages(result.messages),
                  },
                )
              }
            >
              Restaurar agora
            </Button>
          </div>
        </div>
      )}
    </Dialog>
  );
}

function Info({ label, value }: { label: string; value: string }): ReactNode {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-muted">{label}</dt>
      <dd className="truncate font-mono text-sm text-content">{value}</dd>
    </div>
  );
}
